import { useEffect, useRef, useState } from "react";
import type { ReactNode, SyntheticEvent } from "react";
import QuantityInput from "./QuantityInput";
import { ProductRecommendations } from "../recommendations";
import {
  resolveMinQuantitySimple,
  resolveStep,
} from "../../utils/productQuantity";
import { API_PRIVATE_BASE } from "../../utils/apiBase";
import type { CartMap, Product, RecommendationItem } from "../../types";

interface QuantityChangeOptions {
  source?: "catalog" | "recommendation" | string;
}

interface ProductGridProps {
  promotedProducts?: Product[];
  regularProducts?: Product[];
  cart: CartMap;
  recommendationsByProduct?: Record<number, RecommendationItem[]>;
  recommendationsLoadingByProduct?: Record<number, boolean>;
  onQuantityChange: (
    product: Product,
    value: number,
    options?: QuantityChangeOptions,
  ) => void;
  headerActions?: ReactNode;
  filters?: ReactNode;
  pagination?: ReactNode;
  baseMinQuantity?: number;
  lastModifiedProductId?: number | null;
}

const ProductGrid = ({
  promotedProducts = [],
  regularProducts = [],
  cart,
  recommendationsByProduct = {},
  recommendationsLoadingByProduct = {},
  onQuantityChange,
  headerActions,
  filters,
  pagination,
  baseMinQuantity = 0,
  lastModifiedProductId = null,
}: ProductGridProps) => {
  const [imageUrls, setImageUrls] = useState<Record<number, string>>({});
  const imageUrlsRef = useRef<Record<number, string>>({});
  const hasPromoted = promotedProducts.length > 0;
  const hasRegular = regularProducts.length > 0;
  const hasAny = hasPromoted || hasRegular;

  useEffect(() => {
    imageUrlsRef.current = imageUrls;
  }, [imageUrls]);

  useEffect(() => {
    const loadProductImages = async () => {
      const recommendationProducts: Product[] = Object.values(
        recommendationsByProduct,
      ).flatMap((items) =>
        (Array.isArray(items) ? items : [])
          .map((item) => item?.product)
          .filter((product): product is Product => Boolean(product)),
      );

      const allProducts: Product[] = [
        ...promotedProducts,
        ...regularProducts,
        ...recommendationProducts,
      ];

      const uniqueProducts = Array.from(
        new Map<number, Product>(
          allProducts
            .filter((product) => Number.isInteger(Number(product?.id)))
            .map((product) => [Number(product.id), product]),
        ).values(),
      );

      for (const product of uniqueProducts) {
        if (imageUrlsRef.current[product.id] || !product.hasImage) {
          continue;
        }

        try {
          const token = window.localStorage.getItem("authToken");
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const imageEndpoint = `${API_PRIVATE_BASE}/products/${product.id}/image`;

          const response = await fetch(imageEndpoint, {
            headers,
            credentials: "omit",
          });

          if (response.ok) {
            const contentType = response.headers.get("Content-Type") || "";
            if (!contentType.toLowerCase().startsWith("image/")) {
              continue;
            }
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            setImageUrls((prev) => ({
              ...prev,
              [product.id]: blobUrl,
            }));
          } else {
            console.warn(
              `Błąd ${response.status} podczas ładowania zdjęcia dla produktu ${product.id}`,
            );
          }
        } catch (err) {
          console.warn(
            `Nie udało się załadować zdjęcia dla produktu ${product.id}:`,
            err,
          );
        }
      }
    };

    if (hasAny) {
      void loadProductImages();
    }
  }, [promotedProducts, regularProducts, recommendationsByProduct, hasAny]);

  useEffect(() => {
    return () => {
      Object.values(imageUrlsRef.current).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  const renderProductCard = (product: Product) => {
    const selected = cart[product.id]?.quantity || 0;
    const step = resolveStep(product);
    const minQuantity = resolveMinQuantitySimple(product, baseMinQuantity);
    const imageUrl = imageUrls[product.id];
    const recommendations = recommendationsByProduct[product.id] || [];
    const recommendationsLoading = Boolean(
      recommendationsLoadingByProduct[product.id],
    );

    return (
      <div key={product.id} className="product-card-stack">
        <article className="product-card">
          {imageUrl && (
            <div className="product-image-container">
              <img
                src={imageUrl}
                alt={product.displayName || product.name}
                className="product-image"
                onError={(event: SyntheticEvent<HTMLImageElement>) => {
                  if (event.currentTarget.parentElement) {
                    event.currentTarget.parentElement.style.display = "none";
                  }
                }}
              />
            </div>
          )}
          <header>
            <h3>{product.displayName || product.name}</h3>
            <span className="price">
              {product.price.toFixed(2)} zł / {product.unit}
            </span>
          </header>
          <p className="description">{product.description}</p>
          <p className="availability">{product.salesDaysLabel}</p>
          <QuantityInput
            value={selected}
            onChange={(value) => onQuantityChange(product, value)}
            minQuantity={minQuantity}
            step={step}
          />
        </article>
        {selected > 0 && product.id === lastModifiedProductId && (
          <div className="product-card-recommendations">
            <ProductRecommendations
              items={recommendations}
              isLoading={recommendationsLoading}
              cart={cart}
              onQuantityChange={onQuantityChange}
              baseMinQuantity={baseMinQuantity}
              layout="grid"
              imageUrls={imageUrls}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="catalog">
      <div className="catalog-header">
        <h2>Katalog wypieków</h2>
        {headerActions}
      </div>
      {filters}
      {!hasAny ? (
        <p className="catalog-empty">Brak produktów do wyświetlenia.</p>
      ) : (
        <>
          {hasPromoted && (
            <>
              <h3 className="catalog-subheading">Promowane</h3>
              <div className="catalog-grid">
                {promotedProducts.map(renderProductCard)}
              </div>
            </>
          )}
          {hasRegular && (
            <>
              {hasPromoted && <h3 className="catalog-subheading">Pozostałe</h3>}
              {pagination}
              <div className="catalog-grid">
                {regularProducts.map(renderProductCard)}
              </div>
              {pagination}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default ProductGrid;
